import { experience, products, projects, services, skills, stories, workshopBySlug } from "../../../content";
import type {
	Achievement,
	ExperienceEntry,
	Product,
	Project,
	Service,
	SkillCategory,
	Story,
	Workshop
} from "../../../types/content";
import { nonCredlyCerts } from "../../../utils/nonCredlyCerts";
import type { NonCredlyCert } from "../../../utils/nonCredlyCerts";

/**
 * ============================================================================
 * What "the AI work" IS — the one place the selection is made (D-095).
 * ============================================================================
 *
 * Extracted out of `AiInnovation.tsx` because it now has four consumers: the
 * `/ai-innovation` page, the Home teaser, `routeMeta` (which counts it into the
 * route's description) and the dashboard figure registry (which counts it into
 * `ai:credentials` and `ai:builds`). Four copies of "what counts as AI work" is
 * four things that drift, and D-089 is this repository's record of what that
 * costs: five credentials sat at `"category": "ai"` in the JSON for months while
 * the section that renders them read a different field entirely.
 *
 * So the rule lives here once and everything reads it.
 *
 * WHAT IS DERIVED AND WHAT IS NAMED. Credentials, builds and skills are
 * selected by a field the layer already carries, so a new AI credential or a new
 * AI build joins every consumer the day it is added. The story, the workshop,
 * the service and the role have no such field and are named by id — and each
 * one THROWS if the id stops resolving, because a hub whose links quietly
 * evaporate is worse than no hub, and `prerender.mjs` executes this module.
 *
 * ONE CREDENTIAL IS DELIBERATELY UNREACHABLE FROM HERE. `certifications.json`
 * carries `ibm-ai-healthcare` at `status: "pending-details"`. The list below is
 * built from `nonCredlyCerts` — the RENDERED card set — rather than from the
 * JSON, so the publish gate has already excluded it and no consumer of this
 * module has to remember that it exists.
 */

const AI_STORY_ID = "ai-compliance-framework";
const AI_WORKSHOP_SLUG = "ai-automation";
const AI_SERVICE_ID = "ai-enabled-delivery";
const AI_SKILL_GROUP_ID = "ai-delivery";
/** The role whose achievements carry the shipped AI integrations. */
const AI_ROLE_ID = "shyam-steel-2024";

const required = <T>(value: T | undefined, what: string): T => {
	if (value === undefined) {
		throw new Error(
			`aiItems: ${what} no longer resolves. The AI section is a hub over content that lives elsewhere; ` +
				"a link with nothing behind it is worse than no hub, so this fails the build instead."
		);
	}
	return value;
};

export const aiStory: Story = required(
	stories.find((story: Story) => story.id === AI_STORY_ID),
	`the story "${AI_STORY_ID}"`
);

export const aiWorkshop: Workshop = required(workshopBySlug(AI_WORKSHOP_SLUG), `the workshop "/${AI_WORKSHOP_SLUG}"`);

export const aiService: Service = required(
	services.find((service: Service) => service.id === AI_SERVICE_ID),
	`the service "${AI_SERVICE_ID}"`
);

export const aiSkills: SkillCategory = required(
	skills.categories.find((category: SkillCategory) => category.id === AI_SKILL_GROUP_ID),
	`the skill group "${AI_SKILL_GROUP_ID}"`
);

const aiRole: ExperienceEntry = required(
	experience.find((role: ExperienceEntry) => role.id === AI_ROLE_ID),
	`the role "${AI_ROLE_ID}"`
);

/**
 * The achievement lines on that role which are about AI, selected by matching
 * the layer's own words rather than by index. An index is a reference that
 * survives an insertion and starts pointing at something else — silently, and
 * on a page whose whole contract is that every line is substantiated.
 */
const AI_ACHIEVEMENT_MATCH = /AI-assisted compliance intelligence|AI-based interview screening/;

/**
 * Where a line SHIPPED — the product in `products.json` that carries the work
 * the sentence describes, for the lines that have one.
 *
 * THE JOIN IS THE LAYER'S, NOT AN EDITORIAL PAIRING. `sft-evolve-hrms` lists
 * "Third-party BGV and AI-based interview screening integrated into hiring
 * workflows" among its own outcomes, so the achievement and the product are
 * already two records of one piece of work and this only draws the line between
 * them. That is the difference D-101 marks between a filter and a literal that
 * will go stale: the pairing is checkable against the content layer, and the
 * assertion below is what makes a wording change on either side fail the build
 * rather than quietly unlink the card.
 *
 * ABSENCE IS THE NORMAL STATE, as it is in `awardImages.ts`. The compliance
 * framework is a framework rather than a product and `products.json` has no
 * entry for it; that card carries the Career Stories link instead, which is
 * where the account of it actually lives. A line with no product renders as it
 * always did.
 */
interface Shipped {
	/** Matches the achievement line, on the layer's own words — never an index. */
	readonly match: RegExp;
	/** The `products.json` id whose own outcomes record the same work. */
	readonly productId: string;
}

/* NAMED, so the two predicates below can annotate their parameter. `.eslintrc`
   sets `arrow-parens: as-needed` and Prettier adds the parentheses back, so an
   un-annotated single argument is a rule the two tools cannot both satisfy —
   CONTEXT item 6c records the same deadlock for `quotes`. An annotation ends
   it, and reads better than the inline object literal did. */
const SHIPPED_IN: readonly Shipped[] = [{ match: /AI-based interview screening/, productId: "sft-evolve-hrms" }];

/** An AI achievement line, and the product it shipped in where the layer has one. */
export interface AiAchievement {
	text: string;
	product: Product | null;
}

const shippedIn = (text: string): Product | null => {
	const pairing = SHIPPED_IN.find((entry: Shipped) => entry.match.test(text));
	if (!pairing) return null;
	return required(
		products.find((product: Product) => product.id === pairing.productId),
		`the product "${pairing.productId}"`
	);
};

export const aiAchievements: AiAchievement[] = aiRole.achievements
	.map((achievement: Achievement) => achievement.text)
	.filter((text: string) => AI_ACHIEVEMENT_MATCH.test(text))
	.map((text: string) => ({ text, product: shippedIn(text) }));

if (aiAchievements.length === 0) {
	throw new Error(
		"aiItems: neither AI achievement was found on the current role. The layer's wording changed and the AI " +
			"section would have rendered an empty block claiming there is delivery work behind it."
	);
}

/* THE OTHER DIRECTION, because the guard above cannot see it (D-065). Every
   pairing must have found its line. Without this a reworded achievement leaves
   `SHIPPED_IN` matching nothing, the card simply stops being a link, and every
   check on this page still passes — which is D-089's stale matcher exactly: the
   literal was true the day it was written and the content moved underneath it.
   The failure has to be loud, so it fails the build; `prerender.mjs` executes
   this module. */
const unmatched: string[] = SHIPPED_IN.filter(
	(entry: Shipped) => !aiAchievements.some((achievement: AiAchievement) => entry.match.test(achievement.text))
).map((entry: Shipped) => String(entry.match));

if (unmatched.length) {
	throw new Error(
		`aiItems: ${unmatched.join(", ")} matched no AI achievement on the current role. The line was reworded, ` +
			"so the card that links to the product it shipped in would have silently stopped linking anywhere."
	);
}

/** Newest first; ties broken by title so the order is stable across builds. */
export const aiCredentials: NonCredlyCert[] = nonCredlyCerts
	.filter((cert: NonCredlyCert) => cert.category === "AI")
	.sort((a: NonCredlyCert, b: NonCredlyCert) => b.year - a.year || a.title.localeCompare(b.title));

export const aiProjects: Project[] = projects.filter((project: Project) => project.category === "ai-innovation");
